import React, { useState, useEffect } from 'react';
import { makeClient } from './scheduleClient';
import { format, startOfWeek, addDays, isSameDay, parseISO, isAfter } from 'date-fns';
import { Calendar, Clock, MapPin, User, ChevronLeft, ChevronRight, Settings, TrendingUp, Star, DollarSign, Ban, UserCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ClaimEventDialog from './ClaimEventDialog';
import EmployeeSelector from './EmployeeSelector';
import PaymentBonuses from './PaymentBonuses';
import { useAuth } from '@/contexts/AuthContext';
import EventDetailDialog from './EventDetailDialog';
import MonthlyCalendarDialog from './MonthlyCalendarDialog';
import BlackoutCalendarDialog from './BlackoutCalendarDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/schedule`;
const axios = makeClient();

const EVENT_TYPE_COLORS = {
  'Trivia': { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', badge: 'bg-green-500' },
  'Karaoke': { bg: 'bg-pink-50', border: 'border-pink-400', text: 'text-pink-700', badge: 'bg-pink-500' },
  'Music Bingo': { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', badge: 'bg-blue-500' },
  'Special': { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', badge: 'bg-purple-500' },
};

const SchedulingPage = ({ go }) => {
  const { user: hubUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [monthlyCalendarOpen, setMonthlyCalendarOpen] = useState(false);
  const [blackoutCalendarOpen, setBlackoutCalendarOpen] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showBonuses, setShowBonuses] = useState(null); // event id to show bonuses for
  const [loading, setLoading] = useState(true);
  const [loggedInHost, setLoggedInHost] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEventDetail, setSelectedEventDetail] = useState(null);
  const [authChecked, setAuthChecked] = useState(false); // Track if we've checked auth state
  const [claimEligibility, setClaimEligibility] = useState({}); // {event_id: {status, primary_employee_id, opens_at}}

  // Who is signed in comes from the cloud login; claims are checked again on the server.
  useEffect(() => {
    const who = async () => {
      if (hubUser) {
        const admin = ['account_owner', 'admin', 'master_admin'].includes(hubUser.role);
        let name = hubUser.name;
        let isAdmin = admin;
        try {
          const res = await axios.get(`${API}/employees`);
          const me = res.data.find(h => h.user_id === (hubUser._id || hubUser.id));
          if (me?.name) name = me.name;
          if (me?.is_admin) isAdmin = true;
        } catch (err) { console.warn('[Schedule] employee lookup failed:', err.message); }
        setLoggedInHost({ id: (hubUser._id || hubUser.id), name, email: hubUser.email, is_admin: isAdmin });
        setSelectedEmployee((hubUser._id || hubUser.id));
      }
      setAuthChecked(true);
    };
    who();
  }, [hubUser]);

  // Fetch data after auth check is complete
  useEffect(() => {
    if (authChecked) {
      fetchData();
    }
  }, [authChecked]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsRes, venuesRes, employeesRes, eligibilityRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/venues`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/events/claim-eligibility`)
      ]);
      setEvents(eventsRes.data);
      setVenues(venuesRes.data);
      setEmployees(employeesRes.data);
      setClaimEligibility(eligibilityRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date) => {
    return startOfWeek(date, { weekStartsOn: 0 });
  };

  const getCurrentWeekDays = () => {
    const weekStart = getWeekStart(selectedDate);
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = parseISO(event.date);
      return isSameDay(eventDate, day) && isAfter(eventDate, new Date());
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getVenueName = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    return venue ? venue.name : 'Unknown Venue';
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId || e.user_id === employeeId);
    return employee ? employee.name : 'Unknown';
  };

  const getPrimaryNames = (elig) => {
    const ids = elig?.primary_employee_ids || (elig?.primary_employee_id ? [elig.primary_employee_id] : []);
    return ids.map(getEmployeeName).join(' & ');
  };

  const userIsPrimary = (elig) => {
    const ids = elig?.primary_employee_ids || (elig?.primary_employee_id ? [elig.primary_employee_id] : []);
    return !!loggedInHost && ids.includes(loggedInHost.id);
  };

  const handleClaimEvent = (event) => {
    if (!loggedInHost) {
      toast.error('Please login first');
      return;
    }
    setSelectedEvent(event);
    setClaimDialogOpen(true);
  };

  const confirmClaimEvent = async () => {
    setClaimDialogOpen(false);
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/claim`, {});
      toast.success('Event claimed successfully!');
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim event');
    }
  };

  const handleUnclaimEvent = async (event) => {
    if (!window.confirm('Unclaim this event?')) return;
    try {
      await axios.post(`${API}/events/${event.id}/unclaim`);
      toast.success('Event unclaimed successfully');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unclaim event');
    }
  };

  const navigateWeek = (direction) => {
    setSelectedDate(prev => addDays(prev, direction * 7));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const weekDays = getCurrentWeekDays();

  const handleLoginSuccess = (host) => {
    setLoggedInHost(host);
    setSelectedEmployee(host.id);
  };

  // Show loading while checking auth state OR loading data
  if (!authChecked || loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">
            {!authChecked ? 'Checking authentication...' : 'Loading schedule...'}
          </p>
        </div>
      </div>
    );
  }

  // Show loading if SSO hasn't resolved yet
  if (!loggedInHost) {
    return (<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50"><div className="text-center"><img src="/hat-logo.png" alt="Loading" className="w-16 h-16 mx-auto mb-4 animate-pulse" /><p className="text-sm text-slate-400">Setting up your schedule...</p></div></div>);
  }

  return (
    <div data-testid="scheduling-page">
      {/* Header */}
      <header className="bg-card border border-border rounded-2xl shadow-sm mb-6">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2">
                <img 
                  src="/hat-logo.png" 
                  alt="BIG Hat Entertainment" 
                  className="h-12 w-12 object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Event Scheduler</h1>
                <p className="text-sm text-muted-foreground">
                  Welcome, <span className="font-semibold text-primary">{loggedInHost.name}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => go('profile')}
                variant="outline"
                className="flex items-center space-x-2 hover:bg-purple-50 hover:text-purple-600 transition-smooth"
                data-testid="profile-btn"
              >
                <UserCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Profile</span>
              </Button>
              {loggedInHost.is_admin && (
                <Button
                  onClick={() => go('admin')}
                  variant="outline"
                  className="flex items-center space-x-2 hover:bg-primary hover:text-primary-foreground transition-smooth"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div>

        {/* Week Navigation */}
        <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => navigateWeek(-1)}
              variant="outline"
              size="icon"
              className="hover:bg-primary hover:text-primary-foreground transition-smooth"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              onClick={goToToday}
              variant="outline"
              className="px-6 hover:bg-primary hover:text-primary-foreground transition-smooth"
            >
              Today
            </Button>
            <Button
              onClick={() => navigateWeek(1)}
              variant="outline"
              size="icon"
              className="hover:bg-primary hover:text-primary-foreground transition-smooth"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-semibold text-foreground">
              {format(weekDays[0], 'MMM d')} - {format(weekDays[6], 'MMM d, yyyy')}
            </h2>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setBlackoutCalendarOpen(true)}
              className="flex items-center space-x-2 bg-gray-800 text-white hover:bg-gray-900 border-gray-800"
            >
              <Ban className="h-4 w-4" />
              <span>Blackout</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setMonthlyCalendarOpen(true)}
              className="flex items-center space-x-2 hover:bg-primary hover:text-primary-foreground transition-smooth"
            >
              <Calendar className="h-4 w-4" />
              <span>Calendar</span>
            </Button>
          </div>
        </div>

        {/* Weekly Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {weekDays.map((day, idx) => {
            const dayEvents = getEventsForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div key={idx} className="flex flex-col">
                <div className={`text-center p-3 rounded-t-xl border-2 ${
                  isToday
                    ? 'bg-primary text-primary-foreground border-primary shadow-glow'
                    : 'bg-card border-border'
                }`}>
                  <div className="text-sm font-medium">{format(day, 'EEE')}</div>
                  <div className={`text-2xl font-bold ${
                    isToday ? 'text-primary-foreground' : 'text-foreground'
                  }`}>
                    {format(day, 'd')}
                  </div>
                </div>

                <div className="flex-1 bg-card border-2 border-t-0 border-border rounded-b-xl p-3 space-y-3 min-h-[200px]">
                  {dayEvents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No events
                    </div>
                  ) : (
                    dayEvents.map((event) => {
                      const colors = EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS['Special'];
                      const isClaimed = !!event.claimed_by;
                      const isClaimedByUser = event.claimed_by === selectedEmployee;
                      const elig = claimEligibility[event.id];
                      const isPrimaryLocked = !isClaimed && elig?.status === 'primary_only';
                      const isUserThePrimary = isPrimaryLocked && userIsPrimary(elig);

                      return (
                        <Card
                          key={event.id}
                          className={`${colors.bg} ${colors.border} border-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] cursor-pointer ${isPrimaryLocked && !isUserThePrimary ? 'opacity-75' : ''}`}
                          onClick={() => {
                            setSelectedEventDetail(event);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <CardContent className="p-3 space-y-2 text-slate-800 [&_.text-muted-foreground]:text-slate-600 [&_.text-foreground]:text-slate-900">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-1">
                                <Badge className={`${colors.badge} text-white`}>
                                  {event.event_type}
                                </Badge>
                                {event.is_special_event && (
                                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" title="Special Event - Giveaway/Promotion" />
                                )}
                              </div>
                              {isClaimed && (
                                <Badge variant="secondary" className="text-xs">
                                  Claimed
                                </Badge>
                              )}
                              {isPrimaryLocked && !isUserThePrimary && (
                                <Lock className="h-4 w-4 text-amber-600" title="Reserved for primary host" />
                              )}
                            </div>

                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-muted-foreground">
                                <Clock className="h-3 w-3 mr-1" />
                                {format(parseISO(event.date), 'h:mm a')}
                              </div>
                              <div className="flex items-start text-sm font-medium text-foreground">
                                <MapPin className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{getVenueName(event.venue_id)}</span>
                              </div>
                              {isClaimed && (
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <User className="h-3 w-3 mr-1" />
                                  {getEmployeeName(event.claimed_by)}
                                </div>
                              )}
                            </div>

                            {!isClaimed ? (
                              isPrimaryLocked && !isUserThePrimary ? (
                                <div className="text-center space-y-1" data-testid={`primary-locked-${event.id}`}>
                                  <div className="flex items-center justify-center text-xs text-amber-700 font-medium">
                                    <Lock className="h-3 w-3 mr-1" />
                                    Reserved for {getPrimaryNames(elig)}
                                  </div>
                                  <p className="text-[10px] text-amber-600">
                                    Opens {new Date(elig.opens_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </p>
                                </div>
                              ) : (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClaimEvent(event);
                                  }}
                                  className="w-full bg-green-600 hover:bg-green-700 text-white transition-smooth"
                                  size="sm"
                                  disabled={!selectedEmployee}
                                  data-testid={`claim-btn-${event.id}`}
                                >
                                  Claim Event
                                </Button>
                              )
                            ) : isClaimedByUser ? (
                              <div className="space-y-2">
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnclaimEvent(event);
                                  }}
                                  variant="outline"
                                  className="w-full border-red-300 text-red-600 hover:bg-red-50 transition-smooth"
                                  size="sm"
                                >
                                  Unclaim
                                </Button>
                                {(event.event_type === 'Trivia' || event.event_type === 'Music Bingo' || event.event_type === 'Karaoke') && (
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowBonuses(showBonuses === event.id ? null : event.id);
                                    }}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white transition-smooth"
                                    size="sm"
                                  >
                                    {showBonuses === event.id ? 'Hide' : 'Track'} Payment
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                className="w-full cursor-not-allowed"
                                size="sm"
                                disabled
                              >
                                Unavailable
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Bonuses Section */}
        {showBonuses && (
          <div className="mt-6">
            <PaymentBonuses 
              event={events.find(e => e.id === showBonuses)} 
              onUpdate={fetchData}
            />
          </div>
        )}

        {/* Legend */}
        <Card className="mt-8 shadow-card border-2">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Event Types</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(EVENT_TYPE_COLORS).map(([type, colors]) => (
                <div key={type} className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded ${colors.badge}`}></div>
                  <span className="text-sm font-medium">{type}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claim Dialog */}
      {selectedEvent && (
        <ClaimEventDialog
          open={claimDialogOpen}
          onOpenChange={setClaimDialogOpen}
          event={selectedEvent}
          venue={venues.find(v => v.id === selectedEvent.venue_id)}
          onConfirm={confirmClaimEvent}
        />
      )}


      {/* Event Detail Dialog */}
      {selectedEventDetail && (() => {
        const elig = claimEligibility[selectedEventDetail.id];
        const locked = !selectedEventDetail.claimed_by && elig?.status === 'primary_only' && !userIsPrimary(elig);
        return (
          <EventDetailDialog
            open={detailDialogOpen}
            onOpenChange={setDetailDialogOpen}
            event={selectedEventDetail}
            venue={venues.find(v => v.id === selectedEventDetail.venue_id)}
            isClaimed={!!selectedEventDetail.claimed_by}
            isClaimedByUser={selectedEventDetail.claimed_by === loggedInHost?.id}
            onClaim={locked ? null : () => handleClaimEvent(selectedEventDetail)}
            onUnclaim={() => handleUnclaimEvent(selectedEventDetail)}
            reservedFor={locked ? getPrimaryNames(elig) : null}
            reservedOpens={locked ? elig?.opens_at : null}
          />
        );
      })()}

      {/* Monthly Calendar Dialog */}
      <MonthlyCalendarDialog
        open={monthlyCalendarOpen}
        onOpenChange={setMonthlyCalendarOpen}
        events={events}
        currentUserId={loggedInHost?.id}
        onEventClick={(event) => {
          setSelectedEventDetail(event);
          setDetailDialogOpen(true);
        }}
      />

      {/* Blackout Calendar Dialog */}
      <BlackoutCalendarDialog
        open={blackoutCalendarOpen}
        onOpenChange={setBlackoutCalendarOpen}
        employeeId={loggedInHost?.id}
        employeeName={loggedInHost?.name}
        events={events.map(e => ({
          ...e,
          venue_name: venues.find(v => v.id === e.venue_id)?.name
        }))}
      />
    </div>
  );
};

export default SchedulingPage;
